// Dependencies
import React from 'react';

// Utils
import { Impression } from '@nerdwallet/analytics/react/impression';
import { Tooltip, Stack, Type, Box } from '@nerdwallet/currency';

import {
  ENTITY_NAME,
  EVENT_TYPE,
  FORM_NAME,
  MORTGAGE_TYPE,
  SECTION_NAME,
} from '../../../lib/analytics-constants';
import {
  ReducerInputsData,
  AmortizationCalculatorField,
} from '../../../lib/utils';
import ControlPanel from '../../config-builder-free/control-panel/control-panel';
import BreakdownDetails from '../../results-panel/breakdown-details/breakdown-details';
import {
  PurchasePriceInput,
  AnnualInterestRateInput,
  LoanTermInput,
  PropertyLocation,
  DownPaymentInput,
  LoanStartDate,
} from '../inputs';

/** TO DO convert breakdown details to be config builder free */
import monthlyBreakdownConfig from '../monthly-breakdown-view-control-panel-config';

import styles from './ac-control-panel-view.module.less';

type AmortizationControlPanelProps = {
  data: Pick<
    ReducerInputsData<AmortizationCalculatorField>,
    'manualInputs' | 'inputs' | 'validations'
  >;
};

const CONTROL_PANEL = 'controlPanel';

/**
 * Location dropdown React.Node
 */
const getLocationInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.US_LOCATION_UPDATED,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
        was_prefilled: true,
        additional_attributes: {
          location_type: 'property_location',
        },
      },
    },
  };

  return (
    <Box>
      <PropertyLocation label="Location" analytics={analyticsPayload} />
    </Box>
  );
};

/**
 * Purchase Price input field
 * @returns InputMoney Node
 */
const getPurchasePriceInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.HOME_VALUE_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
      },
    },
  };
  return (
    <PurchasePriceInput
      label="Home value"
      tooltip="The total amount you expect to pay for a home before closing costs."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Annual Interest Rate input field
 * @returns InputPercent Node
 */
const getAnnualInterestRate = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_INTEREST_RATE_APR_UPDATED,
        was_prefilled: true,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.NEW,
        },
      },
    },
  };
  return (
    <AnnualInterestRateInput
      label="Interest rate"
      tooltip="The cost of borrowing money that’s typically expressed as an annual percentage of the loan."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Down Payment input field
 * @returns InputMoney Node
 */
const getDownPaymentInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.HOME_DOWN_PAYMENT_UPDATED,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
        was_prefilled: true,
      },
    },
  };
  return (
    <DownPaymentInput
      label="Down payment" // put this value correctly
      tooltip="The cash payment you pay upfront when buying a home."
      analytics={analyticsPayload}
    />
  );
};

/**
 * Loan term input field
 */
const getLoanTerm = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      legacy: {
        formName: CONTROL_PANEL,
      },
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_TERM_UPDATED,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
        was_prefilled: true,
        additional_attributes: {
          mortgage_type: MORTGAGE_TYPE.NEW,
        },
      },
    },
  };

  return (
    <Box>
      <Type size="1">
        Loan term
        <Tooltip
          placement="top"
          content="The amount of time you have to pay back the loan. Usually 15 or 30 years for common loan types."
          analytics={{
            entityName: ENTITY_NAME.HELP_BUTTON_INTERACTED,
            eventType: ENTITY_NAME.HELP_BUTTON_INTERACTED,
            payload: {
              additional_attributes: {
                description: 'loan_term',
              },
            },
          }}
        />
      </Type>
      <Box className={styles.numStepperWrapper}>
        <LoanTermInput analytics={analyticsPayload} />
      </Box>
    </Box>
  );
};

/**
 * Loan Start Date input field
 * @returns InputDate Node
 */
const getLoanStartDateInput = (): React.ReactNode => {
  const analyticsPayload = {
    enabled: true,
    payload: {
      bedrock: {
        eventType: EVENT_TYPE.MORTGAGE_LOAN_START_DATE_UPDATED,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
        was_prefilled: true,
      },
    },
  };
  return <LoanStartDate label="Start date" analytics={analyticsPayload} />;
};

/**
 * Amortization Calculator - Control Panel view
 */
const ACControlPanelView: React.FC<AmortizationControlPanelProps> = (props) => {
  const { data } = props;
  // Builds entire panel content
  const panelContent = (
    <Impression
      eventType={EVENT_TYPE.FORM_IMPRESSION}
      entityName={ENTITY_NAME.MRT_AMORTIZATION_CALCULATOR}
      payload={{
        section_name: SECTION_NAME.MRT_AMORTIZATION_CALCULATOR,
        form_name: FORM_NAME.MRT_AMORTIZATION_CALCULATOR,
      }}
    >
      <Stack spacing={3} className={styles.inputFieldWidth}>
        <Type className={styles.headerContent}>Refine your results</Type>
        <BreakdownDetails
          data={data}
          config={monthlyBreakdownConfig}
          className={styles.breakdownDetails}
        />
        <Stack spacing={3} className={styles.content}>
          {getLocationInput()}
          {getPurchasePriceInput()}
          {getAnnualInterestRate()}
          {getDownPaymentInput()}
          {getLoanStartDateInput()}
          {getLoanTerm()}
        </Stack>
      </Stack>
    </Impression>
  );

  return <ControlPanel panelContent={panelContent} />;
};

export default ACControlPanelView;
